// taxe-saisie.js — la saisie des barèmes de taxe de séjour, plusieurs
// logements d'un coup (29/09, point 12 — CHEMIN ARGENT). Module PUR.
//
// Le barème d'un logement (`log.taxe_sejour`) fixe la taxe du devis payé en
// ligne. Règles :
//   · AUCUN montant proposé ni pré-rempli par l'application : taux et plafond
//     viennent de Dimitri (délibérations, espace hébergeur) ;
//   · seuls les logements COCHÉS changent, et seulement leur barème ;
//   · commune, année et source voyagent AVEC le barème (« un barème doit
//     porter sa commune ET son millésime ») — le calcul du prix, lui, ne lit
//     que taux, plafond et mineurs : rien d'autre ne bouge dans le devis ;
//   · bornes identiques au serveur (taux ∈ ]0, 20], plafond ∈ ]0, 50]).

export const BORNES = { tauxMax: 20, plafondMax: 50, anneeMin: 2020, anneeMax: 2100 };

// Un nombre écrit comme on l'écrit à la main : chiffres, virgule ou point
// décimal, et rien d'autre. `Number()` seul accepte « 0x10 » (16), « 1e1 »
// (10), « +5 » ou « Infinity » : un montant du prix payé en ligne ne doit pas
// pouvoir s'écrire ainsi.
const DECIMAL_STRICT = /^\d+([.,]\d+)?$/;
const ANNEE_STRICTE = /^\d{4}$/;

function nombreStrict(v, motif) {
  if (v === null || v === undefined) return NaN;
  const s = String(v).trim();
  if (!motif.test(s)) return NaN;
  return Number(s.replace(",", "."));
}

/** Valide une saisie. Rend { ok, erreurs:[…], bareme } — jamais de valeur inventée. */
export function validerSaisie(saisie) {
  const erreurs = [];
  const taux = nombreStrict(saisie && saisie.taux, DECIMAL_STRICT);
  const plafond = nombreStrict(saisie && saisie.plafond, DECIMAL_STRICT);
  const annee = nombreStrict(saisie && saisie.annee, ANNEE_STRICTE);
  const commune = String((saisie && saisie.commune) || "").trim();
  if (!commune) erreurs.push("la commune est obligatoire");
  if (!Number.isInteger(annee) || annee < BORNES.anneeMin || annee > BORNES.anneeMax)
    erreurs.push("l'année doit être une année à 4 chiffres");
  if (!(taux > 0 && taux <= BORNES.tauxMax)) erreurs.push(`le taux doit être entre 0 et ${BORNES.tauxMax} %`);
  if (!(plafond > 0 && plafond <= BORNES.plafondMax)) erreurs.push(`le plafond doit être entre 0 et ${BORNES.plafondMax} €`);
  if (saisie && saisie.mineurs !== "oui" && saisie.mineurs !== "non")
    erreurs.push("indique si les mineurs sont exonérés (oui ou non)");
  if (erreurs.length) return { ok: false, erreurs, bareme: null };
  return {
    ok: true,
    erreurs: [],
    bareme: {
      taux_pct: taux,
      plafond_eur: plafond,
      mineurs_exoneres: saisie.mineurs === "oui",
      commune,
      annee,
      source: String(saisie.source || "").trim() || null,
    },
  };
}

/**
 * La configuration suivante : le barème posé sur les SEULS logements donnés
 * (chambres de sous-location comprises). Rien d'autre ne change.
 */
export function appliquerBareme(cfg, ids, bareme, { par = null, le = new Date().toISOString() } = {}) {
  const cibles = new Set(ids || []);
  const marque = { ...bareme, saisi_par: par, saisi_le: le };
  const touches = [];
  const props = (cfg && cfg.props ? cfg.props : []).map((p) => ({
    ...p,
    logs: (p.logs || []).map((l) => {
      let log = l;
      if (cibles.has(l.id) && !(l.chambres && l.chambres.length)) {
        log = { ...l, taxe_sejour: { ...marque } };
        touches.push(l.id);
      }
      if (l.chambres && l.chambres.length) {
        log = {
          ...log,
          chambres: l.chambres.map((ch) => {
            if (!cibles.has(ch.id)) return ch;
            touches.push(ch.id);
            return { ...ch, taxe_sejour: { ...marque } };
          }),
        };
      }
      return log;
    }),
  }));
  return { cfg: { ...cfg, props }, touches };
}

/**
 * Ce que l'écran enregistre vraiment : les cases cochées ENCORE PRÉSENTES dans
 * la commune (une case restée cochée pour un logement sorti du groupe ne part
 * pas), et seulement si chacune est trouvée UNE fois dans la configuration.
 * Sinon rien : `{ ok:false, erreur }`, jamais un enregistrement partiel.
 */
export function preparerEnregistrement(cfg, { idsCommune, coches, bareme, par = null, le } = {}) {
  const dansLaCommune = new Set(idsCommune || []);
  const cibles = [...(coches || [])].filter((id) => dansLaCommune.has(id));
  if (!bareme) return { ok: false, erreur: "la saisie est incomplète", cibles };
  if (!cibles.length) return { ok: false, erreur: "aucun logement coché", cibles };
  const { cfg: suivante, touches } = appliquerBareme(cfg, cibles, bareme, { par, ...(le ? { le } : {}) });
  const vus = new Set(touches);
  if (touches.length !== cibles.length || cibles.some((id) => !vus.has(id))) {
    return { ok: false, erreur: "logements introuvables ou en double dans la configuration — rien n'est enregistré", cibles };
  }
  return { ok: true, cfg: suivante, touches, cibles };
}

/**
 * Les lignes à écrire au catalogue `taxe_sejour_baremes` après une saisie
 * groupée (décision Dimitri 29/09 : « les deux » — logements ET catalogue),
 * au format de l'action `taxe_bareme_validate` (celle qu'utilise déjà la fiche
 * logement, TaxeBaremeDetector). La commune est celle du GROUPE ; le serveur
 * la normalise comme le regroupement et range une ligne par code postal :
 * une ligne par code postal des logements enregistrés.
 *
 * Ce que le catalogue ne peut PAS porter (le dire, ne pas le forcer) :
 *   · l'année du barème — aucune colonne ;
 *   · une source qui n'est pas un lien http(s) — la colonne `source_url` est
 *     rendue en lien cliquable par la fiche logement.
 * Rend { ok:true, corps:[…] } ou { ok:false, raison } — jamais une ligne inventée.
 */
export function corpsCatalogue({ commune, codesPostaux, bareme } = {}) {
  const nom = String(commune || "").trim();
  if (!nom) return { ok: false, raison: "la commune de ce groupe est inconnue (adresse illisible)" };
  const cps = [...new Set((codesPostaux || []).filter((cp) => /^\d{5}$/.test(String(cp || ""))))].sort();
  if (!cps.length) return { ok: false, raison: "aucun code postal lisible sur les adresses" };
  if (!bareme) return { ok: false, raison: "la saisie est incomplète" };
  const src = String(bareme.source || "").trim();
  const sourceUrl = /^https?:\/\/\S+$/i.test(src) ? src : undefined;
  return {
    ok: true,
    corps: cps.map((code_postal) => ({
      commune_nom: nom,
      code_postal,
      taux_pct: bareme.taux_pct,
      plafond_eur: bareme.plafond_eur,
      mineurs_exoneres: bareme.mineurs_exoneres === true,
      ...(sourceUrl ? { source_url: sourceUrl } : {}),
      ...(Number.isInteger(Number(bareme.annee)) && bareme.annee !== "" && bareme.annee != null
        ? { annee: Number(bareme.annee) }
        : {}),
    })),
  };
}

/** D'où vient le barème appliqué aujourd'hui ? Pour l'afficher honnêtement. */
export function origineBareme(log) {
  const t = log && log.taxe_sejour;
  if (!t || typeof t !== "object") return null;
  if (t.saisi_le) return { code: "saisi", texte: `saisi le ${String(t.saisi_le).slice(0, 10)}${t.annee ? ` · barème ${t.annee}` : ""}${t.commune ? ` · ${t.commune}` : ""}` };
  return { code: "non_verifie", texte: "posé avant cet écran, non vérifié (ni commune ni année)" };
}
